// A saved floor plan: walls (with openings) + placed-model references. Models
// aren't embedded — placements reference a collection modelId and are rebuilt
// from the collection on load (so plans stay light). Metres throughout.

import type { LengthUnit } from "../model/units";
import type { DimSide, OpeningKind } from "./walls";

export interface SavedWall {
  ax: number;
  az: number;
  bx: number;
  bz: number;
  // Which side the dimension is measured on (0 = centerline). Optional for
  // back-compat with plans saved before per-wall dimension sides existed.
  dimSide?: DimSide;
  // Whether the user pinned dimSide by dragging the pill. When false/absent the
  // wall auto-defaults to its inner face. Optional for back-compat.
  dimSideExplicit?: boolean;
  // Whether each endpoint corner is locked (pinned). Optional (older plans).
  lockA?: boolean;
  lockB?: boolean;
  openings: {
    kind: OpeningKind;
    offset: number;
    width: number;
    height: number;
    sill: number;
    // Doors only — leaf hinge jamb + swing face. Optional (older plans + windows
    // lack them); both default false (left jamb, left-normal swing).
    hingeRight?: boolean;
    swingNeg?: boolean;
  }[];
}

export interface SavedPlacement {
  modelId: string;
  x: number;
  y: number;
  z: number;
  qx: number; // orientation quaternion (supports full gizmo rotation)
  qy: number;
  qz: number;
  qw: number;
  scale: number; // uniform
  // Grouping label — every placement sharing a value is one group (move/select
  // as a unit). Optional (older plans + ungrouped models lack it).
  group?: number;
  // Per-model dimension-visibility override: true = force-shown, false =
  // force-hidden, absent = follow the global toggle. Optional.
  dimsShow?: boolean;
  // Legacy (pre-override) field: a true here meant "individually hidden". Still
  // read on load and migrated to `dimsShow: false`; no longer written.
  dimsHidden?: boolean;
  // Per-model hide (right-click "Hide model"): true = this model is hidden.
  // Absent/false = shown. Optional (older plans + shown models lack it).
  hidden?: boolean;
}

export interface SavedFloorPlan {
  id: string;
  name: string;
  version: 1;
  walls: SavedWall[];
  placements: SavedPlacement[];
  unit: LengthUnit; // measurement display unit
  height?: number; // uniform wall height (metres). Optional; older plans default it.
  // Persisted measure-tool chains (each a polyline of nodes). A node is either a
  // free floor point {x,z} (metres) or one anchored to a model: {i,lx,lz} where
  // `i` is the placement index and (lx,lz) its point in that model's local frame,
  // so the measurement tracks the model. `l: 1` marks a node whose position is
  // locked for measurement value edits. Optional (older plans lack it).
  measure?: { x?: number; z?: number; i?: number; lx?: number; lz?: number; l?: 1 }[][];
  thumbnail?: string; // top-down PNG data URL (optional; older plans lack it)
  createdAt: number;
  updatedAt: number;
}

// ── Validation ─────────────────────────────────────────────────────────────
// Deep structural check so import + sync pulls reject hostile or corrupted
// payloads BEFORE the restore path runs: loadWalls iterates `openings`
// unconditionally and indexes OPENING_DEFAULTS by `kind` (crash on junk),
// wall/opening/placement numerics feed geometry directly (NaN/Infinity would
// poison meshes), and placements go straight into a quaternion + scale.
// Philosophy: REJECT what would crash or produce NaN geometry; stay LENIENT
// about missing optional fields the restore path already defaults (dimSide,
// locks, measure, height, thumbnail…), so legitimate old plans keep loading.
// Dependency-free and linear in payload size — it runs on every load.

const isFin = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
// A world coordinate/length in metres: finite and nowhere near overflow-crafted.
// Real plans live within tens of metres of the origin; 1e6 is absurdly lenient
// while still rejecting Infinity-adjacent junk that breaks thumbnails + framing.
const COORD_LIMIT = 1e6;
const isCoord = (v: unknown): v is number => isFin(v) && Math.abs(v) <= COORD_LIMIT;
const isCoordOrAbsent = (v: unknown): boolean => v === undefined || isCoord(v);
const isBoolOrAbsent = (v: unknown): boolean => v === undefined || typeof v === "boolean";
const isStrOrAbsent = (v: unknown): boolean => v === undefined || typeof v === "string";
const isFinOrAbsent = (v: unknown): boolean => v === undefined || isFin(v);

// Sanity caps on collection sizes — orders of magnitude beyond any real plan,
// but they stop a crafted file from driving millions of mesh builds.
const MAX_WALLS = 20000;
const MAX_PLACEMENTS = 20000;
const MAX_OPENINGS_PER_WALL = 256;
const MAX_MEASURE_CHAINS = 10000;
const MAX_MEASURE_NODES = 10000;

function isValidOpening(o: unknown): boolean {
  if (!o || typeof o !== "object") return false;
  const op = o as Record<string, unknown>;
  return (
    (op.kind === "door" || op.kind === "window") &&
    isCoord(op.offset) &&
    isCoord(op.width) && op.width >= 0 &&
    isCoord(op.height) && op.height >= 0 &&
    isCoord(op.sill) &&
    isBoolOrAbsent(op.hingeRight) && isBoolOrAbsent(op.swingNeg)
  );
}

function isValidWall(w: unknown): boolean {
  if (!w || typeof w !== "object") return false;
  const wall = w as Record<string, unknown>;
  if (!isCoord(wall.ax) || !isCoord(wall.az) || !isCoord(wall.bx) || !isCoord(wall.bz)) return false;
  // dimSide feeds dimension-line geometry math — only the three legal values.
  if (wall.dimSide !== undefined && wall.dimSide !== -1 && wall.dimSide !== 0 && wall.dimSide !== 1)
    return false;
  if (!isBoolOrAbsent(wall.dimSideExplicit) || !isBoolOrAbsent(wall.lockA) || !isBoolOrAbsent(wall.lockB))
    return false;
  const ops = wall.openings;
  return Array.isArray(ops) && ops.length <= MAX_OPENINGS_PER_WALL && ops.every(isValidOpening);
}

function isValidPlacement(p: unknown): boolean {
  if (!p || typeof p !== "object") return false;
  const sp = p as Record<string, unknown>;
  return (
    typeof sp.modelId === "string" &&
    isCoord(sp.x) && isCoord(sp.y) && isCoord(sp.z) &&
    // Orientation quaternion: components of a normalized quaternion are ≤ 1;
    // allow slack for float drift but reject huge finite values that would
    // explode the placement's matrix.
    isFin(sp.qx) && Math.abs(sp.qx) <= 4 &&
    isFin(sp.qy) && Math.abs(sp.qy) <= 4 &&
    isFin(sp.qz) && Math.abs(sp.qz) <= 4 &&
    isFin(sp.qw) && Math.abs(sp.qw) <= 4 &&
    isFin(sp.scale) && sp.scale > 0 && sp.scale <= 1e4 &&
    isFinOrAbsent(sp.group) &&
    isBoolOrAbsent(sp.dimsShow) && isBoolOrAbsent(sp.dimsHidden) && isBoolOrAbsent(sp.hidden)
  );
}

// A measure node: free {x,z} or anchored {i,lx,lz}; setChains defaults any
// missing coord to 0 and degrades an out-of-range anchor index to a free node,
// so only present-but-junk values reject.
function isValidMeasureNode(n: unknown): boolean {
  if (!n || typeof n !== "object") return false;
  const pt = n as Record<string, unknown>;
  return (
    isCoordOrAbsent(pt.x) && isCoordOrAbsent(pt.z) &&
    isCoordOrAbsent(pt.lx) && isCoordOrAbsent(pt.lz) &&
    (pt.i === undefined || (Number.isInteger(pt.i) && (pt.i as number) >= 0))
  );
}

export function isSavedFloorPlan(v: unknown): v is SavedFloorPlan {
  if (!v || typeof v !== "object") return false;
  const p = v as Record<string, unknown>;
  if (typeof p.id !== "string" || typeof p.name !== "string") return false;
  const walls = p.walls;
  if (!Array.isArray(walls) || walls.length > MAX_WALLS || !walls.every(isValidWall)) return false;
  const placements = p.placements;
  if (!Array.isArray(placements) || placements.length > MAX_PLACEMENTS || !placements.every(isValidPlacement))
    return false;
  // `unit` stays a plain string check so future units migrate, not reject.
  if (!isStrOrAbsent(p.unit)) return false;
  if (p.height !== undefined && !(isFin(p.height) && p.height > 0 && p.height <= 1e3)) return false;
  if (p.measure !== undefined) {
    const m = p.measure;
    if (!Array.isArray(m) || m.length > MAX_MEASURE_CHAINS) return false;
    for (const chain of m) {
      if (!Array.isArray(chain) || chain.length > MAX_MEASURE_NODES || !chain.every(isValidMeasureNode))
        return false;
    }
  }
  return isStrOrAbsent(p.thumbnail) && isFinOrAbsent(p.createdAt) && isFinOrAbsent(p.updatedAt);
}
